import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Home, Pencil, MapPin, Calendar as CalendarIcon, Sparkles } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PostalAddressFields, { addressFromApt, EMPTY_ADDRESS } from "../../components/PostalAddressFields";

const emptyForm = { name: "", apt_type: "T0", ...EMPTY_ADDRESS, notes: "" };

const TYPE_LABELS = [
  ["T0", "T0 · Studio"],
  ["T1", "T1"],
  ["T2", "T2"],
  ["T3", "T3"],
  ["T4", "T4"],
  ["VS", "Villa pequena"],
  ["VM", "Villa média"],
  ["VL", "Villa grande / premium"],
];

/**
 * Client-facing apartments CRUD. Uses the same /apartments endpoints — the
 * backend scopes listings to the caller when role=client, so we don't need
 * anything role-specific here.
 *
 * Onboarding mode (`?onboarding=1`): shows progress against the declared
 * num_accommodations from registration.
 */
export default function ClientApartments() {
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const onboarding = params.get("onboarding") === "1";

  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    const { data } = await api.get("/apartments");
    setItems(data.items);
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  // Auto-open the form on first load if user is onboarding and has none yet
  useEffect(() => {
    if (onboarding && loaded && items.length === 0 && !showForm) {
      setShowForm(true);
    }
  }, [onboarding, loaded, items.length, showForm]);

  const declared = useMemo(
    () => (user?.num_accommodations && user.num_accommodations > 0 ? user.num_accommodations : null),
    [user?.num_accommodations]
  );

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  }

  function startCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  }

  function startEdit(apt) {
    setForm({
      name: apt.name || "",
      apt_type: apt.apt_type || "T0",
      ...addressFromApt(apt),
      notes: apt.notes || "",
    });
    setEditingId(apt.id);
    setShowForm(true);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/apartments/${editingId}`, form);
        toast.success("Alojamento atualizado");
      } else {
        await api.post("/apartments", form);
        toast.success("Alojamento adicionado");
      }
      resetForm();
      await load();
      // keep auth user fresh (num_accommodations hint is purely display)
      refreshUser?.();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function remove(id) {
    if (!window.confirm("Eliminar este alojamento?")) return;
    try {
      await api.delete(`/apartments/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const progressPct = declared ? Math.min(100, Math.round((items.length / declared) * 100)) : 0;
  const remaining = declared ? Math.max(0, declared - items.length) : 0;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Os meus imóveis</div>
          <h1 className="font-display text-5xl">Alojamentos</h1>
          <div className="text-sm text-[#a0b2af] mt-2 max-w-xl">
            Mantenha todos os seus alojamentos locais organizados aqui — facilita o agendamento de limpezas.
          </div>
        </div>
        <button onClick={startCreate} className="btn-gold px-5 py-3 text-sm inline-flex items-center gap-2"
                data-testid="client-apt-new">
          <Plus size={16} /> Adicionar alojamento
        </button>
      </div>

      {/* Onboarding progress banner */}
      {declared && items.length < declared && (
        <div className="lux-card p-5 border-[#d4af37]/60" data-testid="onboarding-progress">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[#d4af37]" />
                <div className="eyebrow">
                  {onboarding ? "Vamos registar os seus alojamentos" : "Onboarding em curso"}
                </div>
              </div>
              <div className="mt-2 text-sm">
                Declarou <span className="text-[#d4af37] font-semibold">{declared}</span> alojamentos no registo.
                Adicione os restantes <span className="text-[#d4af37] font-semibold">{remaining}</span> para ativar totalmente o agendamento.
              </div>
              <div className="mt-3 h-2 bg-[#1a4640] rounded-full overflow-hidden">
                <div className="h-full bg-[#d4af37] transition-all duration-500"
                     style={{ width: `${progressPct}%` }}
                     data-testid="onboarding-progress-bar" />
              </div>
              <div className="text-[11px] text-[#a0b2af] mt-2">
                {items.length} de {declared} alojamentos registados ({progressPct}%)
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="lux-card p-6 grid md:grid-cols-2 gap-4" data-testid="client-apt-form">
          <div className="md:col-span-2 eyebrow">
            {editingId ? "Editar alojamento" : "Novo alojamento"}
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Nome / Designação *</label>
            <input required value={form.name}
                   onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                   placeholder='Ex: "Ocean View Funchal", "Santana Cottage"'
                   className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                   data-testid="client-apt-name" />
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Tipologia *</label>
            <select value={form.apt_type}
                    onChange={e => setForm(f => ({ ...f, apt_type: e.target.value }))}
                    className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                    data-testid="client-apt-type">
              {TYPE_LABELS.map(([t, label]) => <option key={t} value={t}>{label}</option>)}
            </select>
          </div>
          <PostalAddressFields value={form} onChange={(v) => setForm(f => ({ ...f, ...v }))} />
          <div className="md:col-span-2 space-y-2">
            <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Notas internas</label>
            <textarea rows={2} value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Ex: 1 quarto + sala, varanda sul, chave no cofre 1234…"
                      className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3"
                      data-testid="client-apt-notes" />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-gold px-6 py-3 text-sm" data-testid="client-apt-submit">
              {editingId ? "Guardar alterações" : "Adicionar alojamento"}
            </button>
            <button type="button" onClick={resetForm} className="px-6 py-3 text-sm text-[#a0b2af]">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && !showForm && (
          <div className="lux-card p-12 text-center text-[#a0b2af] col-span-full" data-testid="client-apt-empty">
            <Home className="mx-auto mb-4 text-[#d4af37]" size={28} />
            <div className="font-display text-xl text-[#fdfbf7] mb-2">Sem alojamentos registados</div>
            <div className="text-sm mb-4">Adicione o primeiro alojamento para começar a agendar limpezas.</div>
            <button onClick={startCreate} className="btn-gold px-5 py-3 text-sm inline-flex items-center gap-2">
              <Plus size={16} /> Adicionar alojamento
            </button>
          </div>
        )}
        {items.map(a => (
          <div key={a.id} className="lux-card p-6" data-testid={`client-apt-card-${a.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="eyebrow">{a.apt_type}</div>
                <div className="font-display text-2xl mt-1">{a.name}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(a)} className="text-[#d4af37] hover:text-white"
                        data-testid={`client-apt-edit-${a.id}`} title="Editar">
                  <Pencil size={16} />
                </button>
                <button onClick={() => remove(a.id)} className="text-[#fca5a5] hover:text-[#ef4444]"
                        data-testid={`client-apt-delete-${a.id}`} title="Eliminar">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {a.address && (
              <div className="text-sm text-[#a0b2af] flex items-start gap-1.5">
                <MapPin size={13} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                <span>{a.address}</span>
              </div>
            )}
            {a.notes && <div className="text-xs text-[#a0b2af] mt-2 italic">{a.notes}</div>}
            <div className="mt-4 pt-4 border-t border-[#1a4640]">
              <Link to={`/cliente/agendar?apartment_id=${a.id}`}
                    className="text-xs text-[#d4af37] hover:underline inline-flex items-center gap-1.5"
                    data-testid={`client-apt-schedule-${a.id}`}>
                <CalendarIcon size={13} /> Agendar limpeza neste alojamento
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
